// The light dots of the six game tabs (R3262, ui-map-interface.md §16.4):
// a dot on a tab says that something under it waits for the commander.
// The dot is the light dot of the thumb dock (`lightDot`), a shape and not
// only an ink, and the tab says its word to a screen reader through its
// label ("CAPTAIN, WAITS"), as the hub's section dot does (R2153).
//
// What waits under each tab:
//
// - CAPTAIN: a cadet waits for a place in the roster, or a captain whose
//   injury ended is fit again. "Fit again" is a change between two empire
//   frames, so `TabDots` remembers it until the commander opens CAPTAIN.
// - FLEET: a hull waits in the pens, a fleet at sea has no mission, or a
//   fleet withdraws (the `withdrawing` reader of `TabWorld`).
// - BUILD: the build queue is empty.
// - WAR: the window of an object that concerns the reader stands open.
//
// DRAFT and ECONOMY carry no dot. A dot never shows on the open tab: to
// open the tab is to see what waits.

import type { Captain } from '../gen/proto/aquaphobia';
import { Mission } from '../gen/proto/aquaphobia';
import { overlay as S } from '../gen/strings';
import { lightDot } from './overlay/thumbdock';
import { tabLabel } from './overlay/tables';
import type { GameTab } from './tabglyphs';

/**
 * Writes the light dot of one tab button and the word its label says to
 * a screen reader. A tab whose dot did not change is not touched. The tab
 * keeps the state in `data-dot`, so the form of `fitTabs`, which writes
 * the inside of the tab again, draws the dot back.
 */
export function writeTabDot(b: HTMLElement, on: boolean): void {
  const want = on ? '1' : '';
  if ((b.dataset.dot ?? '') === want) return;
  b.dataset.dot = want;
  b.querySelector(':scope > .ldot')?.remove();
  if (on) b.insertAdjacentHTML('beforeend', lightDot(true));
  const name = tabLabel(b.dataset.tab ?? '');
  b.setAttribute('aria-label', on ? S.tab_waits_said({ name }) : name);
}

/** What the dots read, off the empire frame, the fleet layer and the
 *  siege windows. */
export interface TabWorld {
  /** The empire frame has arrived: no dot stands before it. */
  empire: boolean;
  captains: readonly Captain[];
  /** The hulls in the pens: a fleet id each, and whether a repair holds it. */
  penned: readonly { fleetId: number; underRepair: boolean }[];
  /** The own fleets at sea. */
  fleets: readonly { id: number; mission: Mission }[];
  /** Does the fleet withdraw? The wire states it on `FleetState` once that
   *  field lands; until then the overlay passes no reader, and no fleet
   *  withdraws. */
  withdrawing?: (fleetId: number) => boolean;
  /** The jobs in the build queue. */
  queueLength: number;
  /** The window of an object that concerns the reader stands open. */
  windowOpen: boolean;
}

/** The dot of each game tab. */
export type TabWaits = Record<GameTab, boolean>;

/** The dots, with the memory of the captains that are fit again. */
export class TabDots {
  /** The captains whose injury the last frame stated. */
  private hurt = new Set<number>();
  /** The captains that are fit again since the commander last opened
   *  CAPTAIN. */
  private fit = new Set<number>();
  /** The first frame only learns who is hurt: a captain that was fit at
   *  the join is not fit AGAIN. */
  private primed = false;

  /** Reads the captains of one empire frame. A captain that was hurt in
   *  the last frame and is fit, at liberty and on the roster in this one
   *  is fit again. */
  read(captains: readonly Captain[]): void {
    const hurt = new Set<number>();
    for (const c of captains) {
      if (c.cadet || c.capturedBy) continue;
      if (c.injuredSecs > 0) hurt.add(c.id);
      else if (this.primed && this.hurt.has(c.id)) this.fit.add(c.id);
    }
    for (const id of this.fit) {
      if (!captains.some((c) => c.id === id)) this.fit.delete(id);
    }
    this.hurt = hurt;
    this.primed = true;
  }

  /** The dot of each tab, with no dot on the open tab `open` ('' while no
   *  menu stands open). An open CAPTAIN takes the news of the captains
   *  that are fit again. */
  waits(w: TabWorld, open: string): TabWaits {
    const out: TabWaits = { BUILD: false, DRAFT: false, FLEET: false, CAPTAIN: false, ECONOMY: false, WAR: false };
    if (open === 'CAPTAIN') this.fit.clear();
    if (!w.empire) return out;
    const withdrawing = w.withdrawing ?? (() => false);
    out.CAPTAIN = this.fit.size > 0 || w.captains.some((c) => c.cadet);
    out.FLEET = w.penned.some((p) => !p.underRepair)
      || w.fleets.some((f) => f.mission === Mission.MISSION_UNSPECIFIED || withdrawing(f.id));
    out.BUILD = w.queueLength === 0;
    out.WAR = w.windowOpen;
    if (open in out) out[open as GameTab] = false;
    return out;
  }
}
